"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import config from "@lib/config";
import { SIDEBAR_STORAGE_KEY } from "./sidebarBootstrap";

const SIDEBAR_STATE_EVENT = "floats:sidebar-state-change";

const subscribeToSidebarState = (onStoreChange: () => void) => {
  window.addEventListener("storage", onStoreChange);
  window.addEventListener(SIDEBAR_STATE_EVENT, onStoreChange);

  return () => {
    window.removeEventListener("storage", onStoreChange);
    window.removeEventListener(SIDEBAR_STATE_EVENT, onStoreChange);
  };
};

const getSidebarState = () => {
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
  } catch {
    return null;
  }
};

const noopSubscribe = () => () => {};

const setDocumentSidebarState = (open: boolean) => {
  document.documentElement.dataset.floatsSidebarState = open ? "open" : "collapsed";
};

export function useSidebarState(isNarrowViewport: boolean) {
  const [narrowSidebarOverride, setNarrowSidebarOverride] = useState<boolean | null>(null);
  const storedSidebarState = useSyncExternalStore(
    subscribeToSidebarState,
    getSidebarState,
    // Browser storage is unavailable during SSR. Render the compact sidebar
    // as the hydration fallback so a saved collapsed state never flashes open.
    () => "collapsed"
  );
  const sidebarOpen = isNarrowViewport
    ? (narrowSidebarOverride ?? false)
    : storedSidebarState
      ? storedSidebarState === "open"
      : true;

  // False only for the hydration render, which uses the server snapshots
  // above. It flips in the same re-render in which the storage and
  // media-query snapshots resolve.
  const hydrated = useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false
  );

  useEffect(() => {
    // The pre-paint bootstrap script already set the real state. Writing the
    // hydration render's server-snapshot value would briefly flip <html> to
    // "collapsed" and could start the width transition.
    if (!hydrated) return;
    setDocumentSidebarState(sidebarOpen);
  }, [hydrated, sidebarOpen]);

  const toggleDrawer = () => {
    const nextSidebarOpen = !sidebarOpen;

    if (isNarrowViewport) {
      setNarrowSidebarOverride(nextSidebarOpen);
      setDocumentSidebarState(nextSidebarOpen);
      return;
    }

    try {
      window.localStorage.setItem(
        SIDEBAR_STORAGE_KEY,
        nextSidebarOpen ? "open" : "collapsed"
      );
    } catch {
      // The responsive default remains available when storage is blocked.
    }

    setDocumentSidebarState(nextSidebarOpen);
    window.dispatchEvent(new Event(SIDEBAR_STATE_EVENT));
  };

  return { sidebarOpen, toggleDrawer };
}

export function SidebarStateStyles() {
  return (
    <style>{`
        html[data-floats-sidebar-state="open"] .dashboard-sidebar .MuiDrawer-paper {
          width: ${config.drawerWidth}px !important;
        }
        html[data-floats-sidebar-state="collapsed"] .dashboard-sidebar .MuiDrawer-paper {
          width: 77px !important;
        }
        html[data-floats-sidebar-state="open"] .dashboard-sidebar {
          --nav-icon-size: var(--icon-size-sm) !important;
          --nav-item-padding-y: var(--space-2) !important;
        }
        html[data-floats-sidebar-state="collapsed"] .dashboard-sidebar {
          --nav-icon-size: var(--icon-size-md) !important;
          --nav-item-padding-y: var(--space-3) !important;
        }
        html[data-floats-sidebar-state="open"] .dashboard-sidebar :is(.MuiListItemText-root, .MuiListSubheader-root, .MuiTypography-root) {
          display: block !important;
        }
        html[data-floats-sidebar-state="collapsed"] .dashboard-sidebar :is(.MuiListItemText-root, .MuiListSubheader-root, .MuiTypography-root) {
          display: none !important;
        }
        html[data-floats-sidebar-state="open"] .dashboard-sidebar .sidebar-collapse-icon,
        html[data-floats-sidebar-state="collapsed"] .dashboard-sidebar .sidebar-expand-icon {
          display: inline-flex !important;
        }
        html[data-floats-sidebar-state="open"] .dashboard-sidebar .sidebar-expand-icon,
        html[data-floats-sidebar-state="collapsed"] .dashboard-sidebar .sidebar-collapse-icon {
          display: none !important;
        }
        html[data-floats-sidebar-state="open"] #mainContent {
          padding-left: ${config.drawerWidth}px !important;
          --sidebar-width: ${config.drawerWidth}px !important;
        }
        html[data-floats-sidebar-state="collapsed"] #mainContent {
          padding-left: 77px !important;
          --sidebar-width: 77px !important;
        }
      `}</style>
  );
}
